import React, { useState, useEffect, useRef } from 'react';

export default function DatePicker({ value, onChange, placeholder, required }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowYearDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
    }
  }, [value]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    onChange({ target: { name: 'date_of_birth', value: formatDate(date) } });
    setIsOpen(false);
    setShowYearDropdown(false);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleYearSelect = (year) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
    setShowYearDropdown(false);
  };

  const isToday = (date) => {
    const today = new Date();
    return date && date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date && date.toDateString() === selectedDate.toDateString();
  };

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date && date > today;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth(currentDate);

  // Generate years for dropdown (100 years back from current year)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 100; i--) {
    years.push(i);
  }

  return (
    <div className="relative" ref={calendarRef}>
      <div className="relative">
        <input
          type="text"
          value={selectedDate ? formatDate(selectedDate) : ''}
          placeholder={placeholder}
          readOnly
          onClick={() => setIsOpen(!isOpen)}
          required={required}
          style={{
            width: '100%',
            height: '53px',
            opacity: 1,
            borderRadius: '8px',
            padding: '16px',
            borderWidth: '1px',
            border: '1px solid rgba(221, 221, 221, 1)',
            fontSize: '16px',
            outline: 'none',
            transition: 'border-color 0.2s',
            marginBottom: '8px',
            boxSizing: 'border-box',
            cursor: 'pointer'
          }}
        />
      </div>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-50">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              ‹
            </button>
            <div className="relative">
              <h3 
                className="text-lg font-semibold text-gray-800 cursor-pointer hover:text-teal-600 transition-colors duration-200"
                onClick={() => setShowYearDropdown(!showYearDropdown)}
                title="Click to select year"
              >
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              {showYearDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10 year-dropdown-center">
                  {years.map((year) => (
                    <div
                      key={year}
                      className={`px-4 py-2 cursor-pointer hover:bg-teal-50 transition-colors duration-200 ${
                        year === currentDate.getFullYear() ? 'bg-teal-100 text-teal-700' : 'text-gray-700'
                      }`}
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              ›
            </button>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">{day}</div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => date && !isFutureDate(date) && handleDateSelect(date)}
                  className={`w-8 h-8 text-sm rounded-full transition-colors duration-200 ${
                    !date ? 'invisible' : ''
                  } ${
                    isToday(date) ? 'bg-teal-100 text-teal-700 font-semibold' : ''
                  } ${
                    isSelected(date) ? 'bg-teal-600 text-white font-semibold' : ''
                  } ${
                    isFutureDate(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'
                  } ${
                    date && !isToday(date) && !isSelected(date) && !isFutureDate(date) ? 'text-gray-700' : ''
                  }`}
                  disabled={!date || isFutureDate(date)}
                >
                  {date ? date.getDate() : ''}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 